import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Sheet, Textarea, Typography } from '@mui/joy';
import { X } from '@phosphor-icons/react';
import { api } from '../../lib/api';

interface Prompt {
  id: string;
  question: string;
  followup: string;
}

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * The NPS question, shown in-product a few weeks after signup.
 *
 * Bottom-**left**, deliberately: the bottom-right corner is the support button
 * and its drawer, and a survey that covers the way to get help is a survey that
 * costs more than it learns.
 *
 * The score posts the moment it is clicked, before the comment is asked for.
 * Roughly half of people never write the comment, and their scores are the
 * ones most worth having — they are the busy ones.
 */
export const NpsFooterPrompt: React.FC<{ sidebarWidth?: number }> = ({ sidebarWidth = 0 }) => {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ prompt: Prompt | null }>('/api/nps/prompt/')
      .then((res) => {
        if (!cancelled) setPrompt(res.data.prompt);
      })
      // Silent: a survey that cannot load is not worth an error message.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!prompt || hidden) return null;

  const submitScore = async (value: number) => {
    setScore(value);
    try {
      await api.post(`/api/nps/prompt/${prompt.id}/submit/`, { score: value });
    } catch {
      // The score is lost, but nagging about it would be worse than the miss.
      setHidden(true);
    }
  };

  const submitComment = async () => {
    try {
      await api.post(`/api/nps/prompt/${prompt.id}/submit/`, { score, comment });
    } catch {
      // Ignored deliberately: the score is already recorded.
    }
    setDone(true);
    setTimeout(() => setHidden(true), 2000);
  };

  const dismiss = async () => {
    setHidden(true);
    try {
      await api.post(`/api/nps/prompt/${prompt.id}/dismiss/`, {});
    } catch {
      // Nothing to do — it will not be shown again this session regardless.
    }
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        position: 'fixed',
        bottom: 24,
        left: { xs: 16, md: sidebarWidth + 24 },
        right: { xs: 16, md: 'auto' },
        width: { xs: 'auto', md: 520 },
        maxWidth: { xs: 'min(520px, calc(100vw - 32px))', md: `calc(100vw - ${sidebarWidth + 48}px)` },
        boxSizing: 'border-box',
        p: 2,
        borderRadius: 'lg',
        boxShadow: 'lg',
        // Below the support button and its drawer, so it can never cover the
        // way to get help.
        zIndex: 1298,
        display: 'grid',
        gap: 1.25,
      }}
      role="dialog"
      aria-label="Product feedback"
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography level="title-sm" sx={{ flex: 1 }}>
          {done ? 'Thank you — that helps.' : score === null ? prompt.question : prompt.followup}
        </Typography>
        <IconButton size="sm" variant="plain" color="neutral" aria-label="Dismiss" onClick={dismiss}>
          <X size={16} />
        </IconButton>
      </Box>

      {!done && score === null && (
        <>
          <Box
            role="group"
            aria-label="Recommendation score, from 0 to 10"
            sx={{
              display: 'grid', gridTemplateColumns: 'repeat(11, minmax(0, 1fr))', gap: 0.5,
              '@media (max-width: 479px)': { display: 'flex', flexWrap: 'wrap' },
            }}
          >
            {SCORES.map((value) => (
              <Button
                key={value}
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={() => submitScore(value)}
                aria-label={`${value} out of 10`}
                sx={{
                  minWidth: 0, minHeight: 44, px: 0,
                  '@media (max-width: 479px)': { flex: '1 0 calc((100% - 20px) / 6)' },
                }}
              >
                {value}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Not likely
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Very likely
            </Typography>
          </Box>
        </>
      )}

      {!done && score !== null && (
        <>
          <Textarea
            size="sm"
            minRows={2}
            placeholder="Optional"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="sm" variant="plain" color="neutral" onClick={() => setHidden(true)}>
              Skip
            </Button>
            <Button size="sm" variant="solid" onClick={submitComment}>
              Send
            </Button>
          </Box>
        </>
      )}
    </Sheet>
  );
};

export default NpsFooterPrompt;
